import { useEffect, useRef, useState } from "react";

/**
 * Measures parent width and computes displayScale = min(1, parentWidth / intrinsicWidth).
 * Returns { outerRef, displayScale } — attach outerRef to the *parent* container
 * where the canvas should fit (usually the <main> area).
 *
 * The canvas itself should render at its intrinsic size and be wrapped in a
 * space-reserving div; see EditorPage/CsvBatchPage usage.
 */
export function useCanvasScale(intrinsicWidth) {
  const outerRef = useRef(null);
  const [displayScale, setDisplayScale] = useState(1);

  useEffect(() => {
    if (!outerRef.current || !intrinsicWidth) return;
    const update = () => {
      if (!outerRef.current) return;
      // Account for padding: measure client width and subtract inline padding
      const cs = getComputedStyle(outerRef.current);
      const pad = parseFloat(cs.paddingLeft || "0") + parseFloat(cs.paddingRight || "0");
      const avail = outerRef.current.clientWidth - pad;
      if (avail <= 0) return;
      setDisplayScale(Math.min(1, avail / intrinsicWidth));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(outerRef.current);
    return () => ro.disconnect();
  }, [intrinsicWidth]);

  return { outerRef, displayScale };
}

/**
 * Converts a mouse event into preview-px coordinates for a scaled canvas.
 * Uses the canvas element's intrinsic offsetWidth vs its rendered bounding-rect
 * width, so it's self-correcting regardless of state.
 */
export function eventToPreviewCoords(e, canvasEl) {
  if (!canvasEl) return { x: 0, y: 0, scale: 1 };
  const rect = canvasEl.getBoundingClientRect();
  const intrinsic = canvasEl.offsetWidth || rect.width;
  const scale = intrinsic > 0 ? rect.width / intrinsic : 1;
  const s = scale || 1;
  return {
    x: (e.clientX - rect.left) / s,
    y: (e.clientY - rect.top) / s,
    scale: s,
  };
}
